// react
import React, { useState, useMemo } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Box, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableFooter, TablePagination, IconButton, Typography } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon } from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const EventsTableToolbar = () => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" fontWeight="bold">
          {t("events")}
        </Typography>
      </Grid>
    </Grid>
  );
};

const EventsTableHead = () => {
  const { t } = useTranslation();
  const headCells = useMemo(
    () => [
      { id: "triggerSource", label: t("source_name") },
      { id: "magnitude", label: t("magnitude") },
      { id: "value", label: t("value") },
      { id: "condition", label: t("condition") },
      { id: "timestamp", label: t("generation_datetime") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((cell) => (
          <TableCell key={cell.id}>
            <Typography variant="body2" fontWeight="bold">
              {cell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const handleBack = (e) => onPageChange(e, page - 1);
  const handleNext = (e) => onPageChange(e, page + 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBack} disabled={page === 0}>
        <KeyboardArrowLeftOutlinedIcon />
      </IconButton>
      <IconButton onClick={handleNext} disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
        <KeyboardArrowRightOutlinedIcon />
      </IconButton>
    </Box>
  );
};

export const EventsTable = ({ eventsData }) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - eventsData.length) : 0;

  return (
    <Box>
      <EventsTableToolbar />
      <TableContainer>
        <Table>
          <EventsTableHead />
          <TableBody>
            {(rowsPerPage > 0 ? eventsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : eventsData).map((evt) => (
              <TableRow key={evt.id} hover>
                <TableCell>{evt.triggerSourceName || evt.triggerSource}</TableCell>
                <TableCell>{evt.recordMagnitude}</TableCell>
                <TableCell>
                  {evt.recordValue} {evt.recordUom ?? ""}
                </TableCell>
                <TableCell>
                  {evt.triggerOperator} {evt.triggerValue}
                </TableCell>
                <TableCell>{evt.timestamp ? convertTimestamp(evt.timestamp) : "N/A"}</TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter sx={{ backgroundColor: "grey.100" }}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                colSpan={5}
                count={eventsData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t("rows_per_page")}
                labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};
